export class StoreSelector extends window.BAO.CustomElement() {
  /*
    Test mode will show store selector, even if localStorage exists.
    Test mode skips geoip request and sets country code to ES (Spain).
  */
  testMode = false

  connectedCallback () {
    super.connectedCallback()

    if (this.storedCountry) {
      document.body.setAttribute('data-country', this.storedCountry)
    }

    if (window.location.href.indexOf('#geo') > -1) {
      return this.forceRedirect()
    }

    if (this.testMode) {
      this.setupCountrySwitcherModal('ES')
      return
    }

    this.runLocationFlow()
  }

  forceRedirect () {
    this.detectCountry().then(userCountry => {
      if (!userCountry) return
      this.switchTo(userCountry)
    })
  }

  setupListeners () {
    super.setupListeners()

    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onSearchInput = this.onSearchInput.bind(this)
    this.onConfirmClick = this.onConfirmClick.bind(this)
    this.onCloseClick = this.onCloseClick.bind(this)
    this.onLanguageChange = this.onLanguageChange.bind(this)

    // Delegated on document, rather than bound to the external trigger
    // elements directly: the mobile menu lazy-loads and replaces its own
    // markup on first open, which would otherwise destroy the trigger a
    // direct listener was bound to.
    this.listeners.add(document, 'click', this.onDocumentClick)

    if (this.els?.search?.exists) {
      this.els.search.elements.forEach(searchEl => {
        this.listeners.add(searchEl, 'input', this.onSearchInput)
      })
    }

    if (this.els?.confirm.exists) {
      this.els.confirm.elements.forEach(confirmEl => {
        this.listeners.add(confirmEl, 'click', this.onConfirmClick)
      })
    }

    if (this.els?.close?.exists) {
      this.els.close.elements.forEach(closeEL => {
        this.listeners.add(closeEL, 'click', this.onCloseClick)
      })
    }

    if (this.els?.language?.exists) {
      this.els.language.elements.forEach(languageEl => {
        this.listeners.add(languageEl, 'change', this.onLanguageChange)
      })
    }
  }

  onDocumentClick (event) {
    const trigger = event.target.closest(
      '[data-store-selector-external-el="trigger"]',
    )
    if (!trigger) return

    event.preventDefault()
    this.setupCountrySwitcherModal(this.storedCountry || this.currentCountry, {
      forceOpen: true,
    })
  }

  onSearchInput (event) {
    this.filterList(event.currentTarget.value)
  }

  onConfirmClick (event) {
    event.preventDefault()

    const code = event.currentTarget.dataset.code
    if (!code) return

    const result = this.switchTo(code, this.selectedLocale())

    // Already on this Markets country (e.g. stored a country Shopify
    // rejected like UM, then confirming GB). Sync storage and close.
    if (result === 'noop') {
      this.closeDrawer()
    }
  }

  onCloseClick (event) {
    event.preventDefault()

    this.setStoredCountry(this.currentCountry)
    this.closeDrawer()
  }

  closeDrawer () {
    this.dismissGeo()

    // Move focus out before hiding, so it doesn't stay trapped on a
    // descendant of an aria-hidden container.
    if (this.contains(document.activeElement)) {
      document.activeElement.blur()
    }

    this.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('util-StoreSelectorOpen')

    if (document.body.classList.contains('util-DrawersOpen')) return
    document.body.classList.remove('util-SiteOverflowed')
  }

  onLanguageChange (event) {
    const locale = event.currentTarget.value
    const form = event.currentTarget.form
    const input = form?.querySelector('input[name="locale_code"]')
    if (input) input.value = locale

    this.setConfirmEnabled(true)
  }

  /**
   * @param {string} countryCode
   * @param {{ forceOpen?: boolean }} [options]
   */
  setupCountrySwitcherModal (countryCode, options = {}) {
    const code = (countryCode || '').toUpperCase()

    // Same-site Markets country: apply ?country= instead of opening the drawer
    if (!options.forceOpen && this.ensureMarketCountry(code)) {
      return
    }

    // Prefer Shopify's active country when opening manually so a rejected
    // stored country (e.g. UM) does not look selected over the real market.
    const selected =
      options.forceOpen && this.currentCountry
        ? this.currentCountry
        : code || this.currentCountry

    this.buildList(selected)
    this.setAttribute('aria-hidden', 'false')
    document.body.classList.add('util-SiteOverflowed', 'util-StoreSelectorOpen')
    this.releaseSearchFocus()
  }

  sortedCountries () {
    return Object.entries(this.countries).sort(([, a], [, b]) =>
      a.name.localeCompare(b.name),
    )
  }

  countriesWithSavedFirst (countries) {
    const saved = (this.savedCountry || '').toUpperCase()
    if (!saved || !countries.length) return countries

    const savedIndex = countries.findIndex(
      ([code]) => code.toUpperCase() === saved,
    )
    if (savedIndex <= 0) return countries

    const ordered = countries.slice()
    const [savedEntry] = ordered.splice(savedIndex, 1)
    ordered.unshift(savedEntry)
    return ordered
  }

  buildList (selectedCode) {
    const code = (selectedCode || '').toUpperCase()
    this.savedCountry = code
    this.currentSelection = code
    this.allCountries = this.sortedCountries()
    this.renderList(this.allCountries)
    this.setConfirmFor(this.currentSelection)
    this.syncLanguageSelect(this.currentSelection)
    this.setConfirmEnabled(false)

    if (this.els?.search?.exists) {
      this.els.search.elements.forEach(searchEl => {
        searchEl.value = ''
      })
    }
  }

  filterList (query) {
    const term = (query || '').trim().toLowerCase()

    if (!term) {
      this.renderList(this.allCountries)
      return
    }

    const filtered = this.allCountries.filter(
      ([, value]) =>
        value.name.toLowerCase().includes(term) ||
        (value.alternateNames?.length &&
          value.alternateNames.some(name => name.toLowerCase().includes(term))),
    )

    this.renderList(filtered)
  }

  renderList (countries) {
    if (!this.els?.list?.exists) return

    const listEl = this.els.list.element
    listEl.innerHTML = this.countriesWithSavedFirst(countries)
      .map(([code, value]) => this.createItemElement(code, value))
      .join('')

    listEl
      .querySelectorAll('[data-store-selector-el="item"]')
      .forEach(itemEl => {
        itemEl.addEventListener('click', () =>
          this.selectItem(itemEl.dataset.code),
        )
      })
  }

  createItemElement (code, value) {
    const { name, site, currency } = value
    const host = this.hostFor(site)
    const isSelected = code.toUpperCase() === this.currentSelection
    const label = currency ? `${name} - ${currency}` : name

    return `<button
      type="button"
      class="sec-StoreSelector_Item"
      data-store-selector-el="item"
      data-code="${code}"
      data-site="${site}"
      data-host="${host}"
      aria-pressed="${isSelected}"
    >
      <span class="sec-StoreSelector_ItemFlag" aria-hidden="true">${this.flagEmoji(
        code,
      )}</span>
      <span class="sec-StoreSelector_ItemName fz-15_21">${label}</span>
      <svg class="sec-StoreSelector_ItemCheck" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M1 4.3 4.3 7.6 11 1" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>`
  }

  flagEmoji (code) {
    if (!code || code.length !== 2) return ''

    const codePoints = code
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0))

    return String.fromCodePoint(...codePoints)
  }

  selectItem (code) {
    this.currentSelection = (code || '').toUpperCase()
    this.setConfirmFor(this.currentSelection)
    // New country: apply its default language. Same country: keep the
    // current / saved language so FR / DE English is not reset.
    this.syncLanguageSelect(this.currentSelection, {
      ignoreOverride: this.currentSelection !== this.currentCountry,
    })
    this.setConfirmEnabled(true)

    if (!this.els?.list?.exists) return

    this.els.list.element
      .querySelectorAll('[data-store-selector-el="item"]')
      .forEach(itemEl => {
        const isSelected =
          itemEl.dataset.code.toUpperCase() === this.currentSelection
        itemEl.setAttribute('aria-pressed', isSelected ? 'true' : 'false')
      })
  }

  setConfirmFor (code) {
    const entry = this.countries[code]
    if (!entry) return

    const host = this.hostFor(entry.site)

    if (!this.els?.confirm?.exists) return

    this.els.confirm.elements.forEach(confirmEl => {
      confirmEl.dataset.code = code
      confirmEl.dataset.host = host
    })
  }

  setConfirmEnabled (enabled) {
    if (!this.els?.confirm?.exists) return

    this.els.confirm.elements.forEach(confirmEl => {
      confirmEl.disabled = !enabled
    })
  }

  selectedLocale () {
    if (!this.els?.language?.exists) return null

    const languageEl =
      this.els.language.element || this.els.language.elements[0]
    return languageEl?.value || null
  }

  releaseSearchFocus () {
    const title = this.querySelector('.sec-StoreSelector_Title')
    if (title) title.focus({ preventScroll: true })

    if (!this.els?.search?.exists) return

    const blurSearch = () => {
      this.els.search.elements.forEach(searchEl => {
        if (document.activeElement === searchEl) searchEl.blur()
      })
    }

    blurSearch()
    window.requestAnimationFrame(blurSearch)
  }

  /*
  |--------------------------------------------------------------------------
  | Store / Markets switching (formerly window.BAO.StoreSwitcher)
  |--------------------------------------------------------------------------
  | - Same host: localisation form (?country= / locale_code)
  | - Different host (FR / DE market domains, US / JP): goToStore
  | - Language is set by the switcher only (FR / DE default in the dropdown)
  */

  get countries () {
    return (window.theme && window.theme.countries) || {}
  }

  get storeHosts () {
    return (window.theme && window.theme.storeHosts) || {}
  }

  get currentHost () {
    return window.location.hostname
  }

  get storedCountry () {
    const value = window.localStorage.getItem(this.countryKey)
    return value ? value.toUpperCase() : null
  }

  get isGeoDismissed () {
    return window.sessionStorage.getItem(this.geoDismissedKey) === 'true'
  }

  get countryKey () {
    return 'BAO-location-sunspel'
  }

  get choiceKey () {
    return 'BAO-store-sunspel'
  }

  get geoDismissedKey () {
    return 'BAO-geo-dismissed-sunspel'
  }

  get marketApplyKey () {
    return 'BAO-market-apply-sunspel'
  }

  get localeOverrideKey () {
    return 'BAO-locale-override-sunspel'
  }

  get localeApplyKey () {
    return 'BAO-locale-apply-sunspel'
  }

  get currentLocale () {
    return (this.dataset.locale || '').toLowerCase()
  }

  hostFor (site) {
    const key = (site || 'UK').toUpperCase()
    return this.storeHosts[key] || this.storeHosts.UK || this.currentHost
  }

  hasEuPathPrefix () {
    return /^\/eu(\/|$)/i.test(window.location.pathname)
  }

  pathWithoutLocalePrefix () {
    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '')
    const path = window.location.pathname

    if (root && (path === root || path.startsWith(`${root}/`))) {
      return path.slice(root.length) || '/'
    }

    return path
  }

  pathWithoutEuPrefix () {
    const stripped = this.pathWithoutLocalePrefix().replace(
      /^\/eu(?=\/|$)/i,
      '',
    )
    return stripped === '' ? '/' : stripped
  }

  /**
   * Path on the destination host. Keep /en only for an English override;
   * otherwise strip it when applying a non-English locale from the switcher.
   * Country-only handoffs leave the current path alone.
   */
  pathForLocale (locale, countryCode) {
    let path = this.pathWithoutEuPrefix()
    if (!locale) return path

    const hasEnglishPrefix = /^\/en(\/|$)/i.test(path)
    const isEnglishOverride =
      locale &&
      this.localeMatches(locale, 'en') &&
      this.hasEnglishOverride(
        countryCode || this.currentSelection || this.currentCountry,
      )

    if (isEnglishOverride && !hasEnglishPrefix) {
      return path === '/' ? '/en' : `/en${path}`
    }

    if (!isEnglishOverride && hasEnglishPrefix) {
      path = path.replace(/^\/en(?=\/|$)/i, '')
      return path === '' ? '/' : path
    }

    return path
  }

  /**
   * Keep the unpublished theme preview when posting the localisation form.
   */
  returnToForLocale (locale, countryCode) {
    const path = this.pathForLocale(locale, countryCode)
    const previewId = this.previewThemeId()
    if (!previewId) return path

    const separator = path.includes('?') ? '&' : '?'
    return `${path}${separator}preview_theme_id=${encodeURIComponent(
      previewId,
    )}`
  }

  redirectHostFor (countryCode) {
    const code = (countryCode || '').toUpperCase()
    const entry = this.countries[code]
    const site = (entry?.site || '').toUpperCase()

    if (site === 'US' || site === 'JP') return this.hostFor(site)

    // From US / JP, only France and Germany leave for the UK shop —
    // via sunspel.fr / sunspel.de. Other UK-shop countries stay here.
    if (!this.isOnSite('UK')) {
      if (code === 'FR' || site === 'FR') return this.hostFor('FR')
      if (code === 'DE' || site === 'DE') return this.hostFor('DE')
    }

    // Stay on this host for UK-shop Markets, including myshopify /
    // consol preview and unpublished theme previews.
    if (
      this.isSharedMarketsHost() ||
      this.isPreviewTheme() ||
      site === 'UK' ||
      site === 'EU' ||
      site === ''
    ) {
      return this.currentHost
    }

    return this.hostFor(site)
  }

  /**
   * Consol / *.myshopify.com preview shares one host for UK / EU (and FR / DE
   * before their live domains are used). Live FR / DE use www.sunspel.fr / .de.
   */
  isPreviewTheme () {
    const role = window.Shopify?.theme?.role
    return (
      !!this.getParam('preview_theme_id') ||
      role === 'development' ||
      role === 'unpublished'
    )
  }

  previewThemeId () {
    return (
      this.getParam('preview_theme_id') ||
      (this.isPreviewTheme() ? window.Shopify?.theme?.id : null)
    )
  }

  isSharedMarketsHost () {
    const host = (this.currentHost || '').toLowerCase()

    if (host.includes('sunspel-us') || host === 'us.sunspel.com') return false
    if (
      host.includes('sunspel-jp') ||
      host === 'www.sunspel.jp' ||
      host === 'sunspel.jp'
    ) {
      return false
    }

    return host.includes('myshopify.com')
  }

  /**
   * Site identity from the current URL. On consol / preview, host alone
   * cannot distinguish UK / EU — those share one host. FR / DE use their
   * own live domains.
   * @returns {string | null}
   */
  currentSite () {
    const shop = (window.Shopify?.shop || '').toLowerCase()
    if (shop.includes('sunspel-us')) return 'US'
    if (shop.includes('sunspel-jp')) return 'JP'

    const host = (this.currentHost || '').toLowerCase()

    if (this.isSharedMarketsHost()) return null
    if (host.includes('sunspel-us') || host === 'us.sunspel.com') return 'US'
    if (
      host.includes('sunspel-jp') ||
      host === 'www.sunspel.jp' ||
      host === 'sunspel.jp'
    ) {
      return 'JP'
    }
    if (host === 'sunspel.fr' || host === 'www.sunspel.fr') return 'FR'
    if (host === 'sunspel.de' || host === 'www.sunspel.de') return 'DE'
    if (host === 'eu.sunspel.com' || host === 'www.eu.sunspel.com') return 'EU'
    if (
      this.hasEuPathPrefix() &&
      (host === 'www.sunspel.com' || host === 'sunspel.com')
    ) {
      return 'EU'
    }
    return 'UK'
  }

  isOnSite (site) {
    const key = (site || 'UK').toUpperCase()

    if (key === 'US' || key === 'JP') {
      return this.currentSite() === key
    }

    if (this.isSharedMarketsHost()) return true

    const current = this.currentSite()
    if (current === 'US' || current === 'JP') return false

    // UK / FR / DE / EU are one shop: market domains on sunspel.fr / .de
    if (key === 'UK' || key === 'FR' || key === 'DE' || key === 'EU') {
      return (
        current === 'UK' ||
        current === 'FR' ||
        current === 'DE' ||
        current === 'EU' ||
        current === null
      )
    }

    return current === key
  }

  setStoredCountry (code) {
    if (!code) return
    const upper = code.toUpperCase()
    window.localStorage.setItem(this.countryKey, upper)
    window.localStorage.setItem(this.choiceKey, 'true')
    document.body.setAttribute('data-country', upper)
  }

  markMarketApplyAttempted (code) {
    if (!code) return
    window.sessionStorage.setItem(
      this.marketApplyKey,
      `${this.currentHost}:${code.toUpperCase()}`,
    )
  }

  hasMarketApplyAttempted (code) {
    if (!code) return false
    return (
      window.sessionStorage.getItem(this.marketApplyKey) ===
      `${this.currentHost}:${code.toUpperCase()}`
    )
  }

  markLocaleApplyAttempted (locale) {
    if (!locale) return
    window.sessionStorage.setItem(
      this.localeApplyKey,
      `${this.currentHost}:${locale.toLowerCase()}`,
    )
  }

  hasLocaleApplyAttempted (locale) {
    if (!locale) return false
    return (
      window.sessionStorage.getItem(this.localeApplyKey) ===
      `${this.currentHost}:${locale.toLowerCase()}`
    )
  }

  hasEnglishOverride (countryCode) {
    const code = (countryCode || '').toUpperCase()
    return this.localeMatches(this.getLocaleOverrides()[code], 'en')
  }

  /**
   * France / Germany default to French / German. Other UK-shop countries
   * default to English in the language switcher. US / JP are left alone.
   * @param {string} countryCode
   * @returns {string | null}
   */
  defaultLocaleFor (countryCode) {
    const code = (countryCode || '').toUpperCase()
    if (code === 'FR') return 'fr'
    if (code === 'DE') return 'de'

    const entry = this.countries[code] || {}
    const site = (entry.site || '').toUpperCase()
    if (site === 'FR') return 'fr'
    if (site === 'DE') return 'de'

    const name = (entry.name || '').toLowerCase()
    if (name === 'france') return 'fr'
    if (name === 'germany') return 'de'

    return null
  }

  isUkShopCountry (countryCode) {
    const site = (this.countries[countryCode]?.site || '').toUpperCase()
    return site !== 'US' && site !== 'JP'
  }

  englishLocaleValue () {
    if (!this.els?.language?.exists) return null

    const languageEl =
      this.els.language.element || this.els.language.elements[0]
    return this.findLanguageOption(languageEl, 'en')
  }

  /**
   * FR → French, DE → German, other UK-shop countries → English.
   * Used when the shopper picks a country in the list.
   */
  defaultLanguagePref (countryCode) {
    const defaultLocale = this.defaultLocaleFor(countryCode)
    if (defaultLocale) return defaultLocale

    if (this.isUkShopCountry(countryCode)) return this.englishLocaleValue()
    return null
  }

  /**
   * Language shown in the switcher for a country: country default, unless
   * English was saved for that country (re-opening the current market).
   */
  languagePrefFor (countryCode) {
    const code = (countryCode || '').toUpperCase()
    const override = this.getLocaleOverrides()[code]
    if (override) return override

    return this.defaultLanguagePref(code)
  }

  localeMatches (left, right) {
    if (!left || !right) return false
    return (
      left.toLowerCase().split('-')[0] === right.toLowerCase().split('-')[0]
    )
  }

  getLocaleOverrides () {
    try {
      const stored = window.localStorage.getItem(this.localeOverrideKey)
      if (!stored) return {}
      const parsed = JSON.parse(stored)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (error) {
      return {}
    }
  }

  setLocaleOverride (countryCode, locale) {
    const code = (countryCode || '').toUpperCase()
    if (!code) return

    const overrides = this.getLocaleOverrides()
    const implicitLocale =
      this.defaultLocaleFor(code) || (this.isUkShopCountry(code) ? 'en' : null)

    if (
      !locale ||
      this.localeMatches(locale, implicitLocale) ||
      !this.localeMatches(locale, 'en')
    ) {
      delete overrides[code]
    } else {
      overrides[code] = locale
    }

    window.localStorage.setItem(
      this.localeOverrideKey,
      JSON.stringify(overrides),
    )
  }

  findLanguageOption (selectEl, locale) {
    if (!selectEl || !locale) return null

    const target = locale.toLowerCase()
    const options = [...selectEl.options]
    const exact = options.find(option => option.value.toLowerCase() === target)
    if (exact) return exact.value

    const prefix = options.find(option =>
      option.value.toLowerCase().startsWith(`${target}-`),
    )
    if (prefix) return prefix.value

    const language = target.split('-')[0]
    const byLanguage = options.find(
      option => option.value.toLowerCase().split('-')[0] === language,
    )
    return byLanguage ? byLanguage.value : null
  }

  /**
   * Locale to apply for a country. FR / DE default to French / German;
   * other UK-shop countries default to English. A stored English
   * override wins. US / JP send no locale.
   * @param {string} countryCode
   * @returns {string | null}
   */
  localeToApply (countryCode) {
    return this.languagePrefFor(countryCode)
  }

  syncLanguageSelect (countryCode, options = {}) {
    if (!this.els?.language?.exists) return

    const locale = options.ignoreOverride
      ? this.defaultLanguagePref(countryCode)
      : this.languagePrefFor(countryCode)
    if (!locale) return

    this.els.language.elements.forEach(languageEl => {
      const match = this.findLanguageOption(languageEl, locale)
      if (!match) return

      languageEl.value = match

      const form = languageEl.form
      const input = form?.querySelector('input[name="locale_code"]')
      if (input) input.value = match
    })
  }

  /**
   * On the matching Markets site, sync Shopify country via ?country=.
   * Language is left as-is — the switcher sets it on confirm. Returns
   * true when this site handles the country (including when already
   * correct, or when a prior apply attempt was made this session).
   * @param {string} countryCode
   * @returns {boolean}
   */
  ensureMarketCountry (countryCode) {
    const code = (countryCode || '').toUpperCase()
    if (!code || !this.countries[code]) return false
    if (!this.isOnSite(this.countries[code].site)) return false

    const countryOk = this.currentCountry && code === this.currentCountry

    if (countryOk) {
      this.setStoredCountry(code)
      return true
    }

    if (this.hasMarketApplyAttempted(code)) {
      // Shopify ignored ?country= (unsupported market). Re-sync storage
      // to the active country so the switcher does not get stuck.
      this.setStoredCountry(this.currentCountry || code)
      return true
    }

    this.switchTo(code, null, { countryOnly: true })
    return true
  }

  dismissGeo () {
    window.sessionStorage.setItem(this.geoDismissedKey, 'true')
  }

  getParam (name) {
    return new URLSearchParams(window.location.search).get(name)
  }

  stripParam (name) {
    const url = new URL(window.location.href)
    if (!url.searchParams.has(name)) return
    url.searchParams.delete(name)
    window.history.replaceState({}, '', url.toString())
  }

  updateSearchParams (params) {
    const searchParams = new URLSearchParams(window.location.search)
    Object.keys(params).forEach(key => searchParams.set(key, params[key]))
    window.location.search = searchParams.toString()
  }

  /**
   * Shopify honours the localisation form more reliably than ?locale=
   * when a previous language cookie is set. Optional targetHost posts
   * the form to the FR / DE market domain so language is set there.
   */
  submitLocalization (countryCode, locale, targetHost) {
    const form = document.getElementById('store-selector-language-form')
    if (!form) {
      if (
        targetHost &&
        targetHost.toLowerCase() !== (this.currentHost || '').toLowerCase()
      ) {
        this.goToStore(targetHost, countryCode, locale)
        return
      }

      const params = { country: countryCode }
      if (locale) params.locale = locale
      this.updateSearchParams(params)
      return
    }

    let countryInput = form.querySelector('input[name="country_code"]')
    if (!countryInput) {
      countryInput = document.createElement('input')
      countryInput.type = 'hidden'
      countryInput.name = 'country_code'
      form.appendChild(countryInput)
    }
    if (countryCode) countryInput.value = countryCode

    const localeInput = form.querySelector('input[name="locale_code"]')
    if (localeInput && locale) localeInput.value = locale

    let returnTo = form.querySelector('input[name="return_to"]')
    if (!returnTo) {
      returnTo = document.createElement('input')
      returnTo.type = 'hidden'
      returnTo.name = 'return_to'
      form.appendChild(returnTo)
    }
    returnTo.value = this.returnToForLocale(locale, countryCode)

    const host = (targetHost || '').toLowerCase()
    if (host && host !== (this.currentHost || '').toLowerCase()) {
      try {
        const action = new URL(
          form.getAttribute('action') || '/localization',
          window.location.origin,
        )
        action.protocol = 'https:'
        action.hostname = targetHost
        form.action = action.toString()
      } catch (error) {
        this.goToStore(targetHost, countryCode, locale)
        return
      }
    }

    form.submit()
  }

  goToStore (targetHost, countryCode, locale) {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete('currency')
    const previewId = this.previewThemeId()
    if (previewId && !searchParams.has('preview_theme_id')) {
      searchParams.set('preview_theme_id', previewId)
    }
    if (countryCode) {
      searchParams.set('country', countryCode.toUpperCase())
    }

    if (locale) searchParams.set('locale', locale)
    else searchParams.delete('locale')

    const path = this.pathForLocale(locale, countryCode)
    const query = searchParams.toString()
    window.location.href = `https://${targetHost}${path}${
      query ? `?${query}` : ''
    }`
  }

  /**
   * @param {string} countryCode
   * @param {string} [selectedLocale]
   * @param {{ countryOnly?: boolean }} [options]
   * @returns {'noop' | 'markets' | 'redirect'}
   */
  switchTo (countryCode, selectedLocale, options = {}) {
    const code = (countryCode || '').toUpperCase()
    const entry = this.countries[code]
    if (!entry) return 'noop'

    const locale = options.countryOnly
      ? null
      : selectedLocale || this.localeToApply(code)

    if (!options.countryOnly) this.setLocaleOverride(code, locale)

    const currentCountry = this.currentCountry
    const countryOk = currentCountry && code === currentCountry
    const localeOk = !locale || this.localeMatches(locale, this.currentLocale)

    const targetHost = this.redirectHostFor(code)
    const hostChanged =
      targetHost &&
      targetHost.toLowerCase() !== (this.currentHost || '').toLowerCase()

    if (this.isOnSite(entry.site) && !hostChanged) {
      if (countryOk && localeOk) {
        this.setStoredCountry(code)
        return 'noop'
      }

      this.setStoredCountry(code)
      this.markMarketApplyAttempted(code)
      if (locale) this.markLocaleApplyAttempted(locale)
      this.submitLocalization(code, locale)
      return 'markets'
    }

    // Different host: GET with ?country= / ?locale= from the switcher.
    // Do not POST locale_code from a market that does not publish that
    // language (FR has no German) — Shopify then falls back to /en.
    this.setStoredCountry(code)
    this.goToStore(targetHost, code, locale)
    return 'redirect'
  }

  /**
   * Handles ?country= / ?locale= handoff from another storefront / Markets switch.
   * @returns {boolean} true when a country or locale param was present
   */
  handleCountryParam () {
    const param = this.getParam('country')
    const localeParam = this.getParam('locale')
    if (!param && !localeParam) return false

    if (param) {
      const code = param.toUpperCase()
      if (this.countries[code]) {
        if (!this.currentCountry || code === this.currentCountry) {
          this.setStoredCountry(code)
        } else {
          // Shopify did not adopt this country (unsupported market). Persist
          // the active country instead and remember the failed attempt.
          this.setStoredCountry(this.currentCountry)
          this.markMarketApplyAttempted(code)
        }
      }

      this.stripParam('country')
    }

    if (localeParam) {
      this.stripParam('locale')

      if (
        !this.localeMatches(localeParam, this.currentLocale) &&
        !this.hasLocaleApplyAttempted(localeParam)
      ) {
        this.markLocaleApplyAttempted(localeParam)
        this.submitLocalization(this.currentCountry, localeParam)
        return true
      }
    }

    return true
  }

  async detectCountry () {
    try {
      const res = await fetch('/browsing_context_suggestions.json').then(
        response => response.json(),
      )
      return res?.detected_values?.country?.handle || null
    } catch (error) {
      console.error('Error detecting country:', error)
      return null
    }
  }

  resolveCountry (code) {
    const upper = (code || '').toUpperCase()
    if (this.countries[upper]) {
      return { code: upper, ...this.countries[upper] }
    }
    return null
  }

  /**
   * Geo / stored-location flow for the first-visit modal.
   */
  async runLocationFlow () {
    try {
      if (this.handleCountryParam()) return

      if (window.Shopify?.designMode) return

      const stored = this.storedCountry
      if (stored && this.countries[stored]) {
        if (
          this.defaultLocaleFor(this.currentCountry) &&
          stored !== this.currentCountry
        ) {
          this.setStoredCountry(this.currentCountry)
          return
        }

        // Same site (UK / FR / DE / EU Markets): apply ?country= when localisation differs
        if (this.ensureMarketCountry(stored)) return

        if (!this.isGeoDismissed) {
          this.setupCountrySwitcherModal(stored)
        }
        return
      }

      const detected = await this.detectCountry()
      const resolved = detected ? this.resolveCountry(detected) : null
      if (resolved) {
        // Same site: apply ?country= rather than only localStorage
        if (this.ensureMarketCountry(resolved.code)) return

        if (!this.isGeoDismissed) {
          this.setupCountrySwitcherModal(resolved.code)
        }
      }
    } catch (error) {
      console.error('Error in runLocationFlow:', error)
    }
  }

  get currentCountry () {
    return (this.dataset.countryCode || this.dataset.store || '').toUpperCase()
  }
}

if (!customElements.get('store-selector')) {
  customElements.define('store-selector', StoreSelector)
}
